import { ancestorCrumbs } from '../utils/collectionFamily';
import { kindKicker } from '../utils/collectionKinds';
import { canManage, canReadCharts } from '../utils/permissions';

/**
 * The collection scope of the shell (docs/platform-standards.md §13): what the
 * crumb, the badge and the tabs say for everything under one collection. The
 * shell component (components/shell/ShellHeader.js) draws whatever this
 * returns and nothing else, so a page never assembles a header of its own —
 * and the next scope (record, account, admin) is another function of the same
 * shape, not another component.
 *
 *   { crumbs: [{ label, to?, icon?, switcher? }],
 *     scope:  { eyebrow, record, kind, title, below },
 *     tabs:   [{ key, label, to, end?, visible }] }
 *
 * `visible` is the rule, already evaluated: the tabs are filtered with the
 * SAME grants the pages enforce (`canManage`, `canReadCharts`), never a second
 * permission source. While the collection is still loading nothing is known,
 * so every tab a person usually has is drawn and corrected on arrival, which
 * is what the old section buttons did (no layout shift on navigation).
 */

export const ALL_COLLECTIONS_CRUMB = { label: 'All collections', to: '/collections' };

export const COLLECTION_EYEBROW = 'Collection';

// The collection scope's sibling sections, in order. `end` marks a route that
// is only active on its own path (Home, the bare collection route).
const COLLECTION_TABS = [
	{ key: 'home', label: 'Home', path: '', end: true, visible: () => true },
	// Items, not List (KPR-818): the page holds the list, the grid, the board and
	// the form. The key stays `list`: data-shell-tab, the switcher and the tests read it.
	{ key: 'list', label: 'Items', path: '/items', visible: () => true },
	{ key: 'reports', label: 'Reports', path: '/reports', visible: ({ accessKnown, collection }) => !accessKnown || canReadCharts(collection) },
	// The collection's cards (KPR-1281, KPE-039): everyone who reads the
	// collection reads its cards, so the tab is there whenever the collection
	// opened (and while it loads); what a reader may do there is the page's
	// own gate. A collection that would not open has no cards to show.
	{ key: 'cards', label: 'Cards', path: '/cards', visible: ({ accessKnown, collection }) => !accessKnown || Boolean(collection) },
	{ key: 'settings', label: 'Settings', path: '/form', visible: ({ accessKnown, collection }) => !accessKnown || canManage(collection) },
];

export function collectionTabs({ collection, collectionId, accessKnown = Boolean(collection) } = {}) {
	const base = `/collections/${collectionId || collection?._id || ''}`;
	return COLLECTION_TABS
		.filter(tab => tab.visible({ accessKnown, collection }))
		.map(({ key, label, path, end }) => ({ key, label, to: `${base}${path || ''}`, end: Boolean(end) }));
}

/**
 * @param {object} args
 * @param {object|null} args.collection   the loaded collection, or null while it loads
 * @param {string} args.collectionId      the route's id
 * @param {string} [args.title]           the badge's title when it is not the collection's name
 * @param {string|Array<{label: string, to?: string}>|null} [args.crumbTail]
 *                                        a page's own last crumb after the collection, or a list of
 *                                        them (an editor under a card: the card, then the editor, KPR-1287)
 * @param {boolean} [args.accessKnown]    whether the person's grants are known: true once the
 *                                        collection loaded, and also when the load FAILED (404,
 *                                        no access), so a collection nobody can open does not
 *                                        draw Reports and Settings tabs it cannot back up
 * @param {boolean} [args.loading]        the collection has not answered yet: the header draws placeholders for its name
 */
export function collectionShellConfig({ collection, collectionId, title, crumbTail = null, accessKnown = Boolean(collection), loading = false } = {}) {
	const name = title || collection?.name || '';
	const kicker = kindKicker(collection);
	const tail = (Array.isArray(crumbTail) ? crumbTail : [crumbTail])
		.map(crumb => (typeof crumb === 'string' ? { label: crumb } : crumb))
		.filter(crumb => crumb && crumb.label);
	const lastLabel = tail.length ? tail[tail.length - 1].label : null;

	const crumbs = [
		{ ...ALL_COLLECTIONS_CRUMB, icon: 'collection' },
		...ancestorCrumbs(collection),
		// The collection is the current object on a section page: plain text, and
		// `switcher` is where the collection switcher (KPR-640) draws its ▾. With a
		// page's own crumb after it, it is a place to go back to, so it links to its list, as the ancestors do.
		{
			label: collection?.name || 'Collection',
			...(tail.length ? { to: `/collections/${collectionId || collection?._id || ''}/items` } : {}),
			switcher: true,
			// What the switcher marks as current and routes from.
			collectionId: collectionId || collection?._id || '',
			...(loading ? { pending: true } : {}),
		},
	];
	// Only the last node is the current object; one before it may link back (the card).
	tail.forEach((crumb, index) => crumbs.push(index < tail.length - 1 && crumb.to ? { label: crumb.label, to: crumb.to } : { label: crumb.label }));

	return {
		crumbs,
		scope: {
			eyebrow: COLLECTION_EYEBROW,
			record: collection || undefined,
			kind: 'collection',
			title: name,
			// The browser tab: the page's last crumb first (an item), then the
			// collection, then the brand (useDocumentTitle adds that).
			documentTitle: lastLabel ? `${lastLabel} · ${name}` : name,
			kicker,
			...(loading ? { loading: true } : {}),
		},
		tabs: collectionTabs({ collection, collectionId, accessKnown }),
	};
}

/**
 * The Settings sub-nav (KPR-637): the sections of the Settings tab as a left
 * list inside the content, in the SAME shape as a tab, `{ key, label, to,
 * visible? }`, and never a second tab row. A section is one value of `?tab=`
 * (Settings itself is the bare route), so every URL is the one it always was.
 * The page resolves which sections exist (the grants it already enforces) and
 * hands them in as `visible`; a section with `visible: false` is left out.
 *
 * @param {object} args
 * @param {string} args.collectionId
 * @param {{key: string, label: string, visible?: boolean}[]} args.sections
 */
export function settingsSubNav({ collectionId, sections = [] } = {}) {
	const base = `/collections/${collectionId || ''}/form`;
	return sections
		.filter(section => section.visible !== false)
		.map(({ key, label }) => ({ key, label, to: key === 'settings' ? base : `${base}?tab=${key}` }));
}

/** The collection's Cards tab (KPR-1281): where its Card Library and element sets live. */
export function collectionCardsPath(collectionId) {
	return `/collections/${collectionId || ''}/cards`;
}

/** A card's page inside the collection that owns it (KPR-1282): its home, `?tab=` an editing section. */
export function collectionCardPath(collectionId, cardId) {
	return `${collectionCardsPath(collectionId)}/${cardId || ''}`;
}

/**
 * Where a new card for this collection is made (KPR-1284): the card page in
 * the collection's shell, which the first save turns into the card's own.
 * `parentCardId` extends that card (Extend into a new card).
 */
export function newCardPath(collectionId, { parentCardId = null } = {}) {
	const base = collectionCardPath(collectionId, 'new');
	return parentCardId ? `${base}?parentCardId=${encodeURIComponent(parentCardId)}` : base;
}

/**
 * The card page's sub-nav (KPR-1282): its sections as a left list inside the
 * content, the same shape as Settings' (`{ key, label, to }`), never a second
 * tab row. The Overview is the bare route; every other section is its
 * `?tab=` value, so each URL is the one the card page always had. The page
 * decides which sections this person has and hands them in; one with
 * `visible: false` or `disabled: true` (a new card's Overview) is left out.
 */
export function cardSubNav({ collectionId, cardId, sections = [] } = {}) {
	const base = collectionCardPath(collectionId, cardId);
	return sections
		.filter(section => section.visible !== false && !section.disabled)
		.map(({ key, label }) => ({ key, label, to: key === 'overview' ? base : `${base}?tab=${key}` }));
}

// The first path segment under a collection, and the tab it belongs to. Item
// pages and their editors sit under Items; the chart, builder and dashboard pages
// under Reports; a card's pages under Cards; the editors reached from Settings under Settings.
const SECTION_OF_SEGMENT = {
	items: 'list', search: 'list',
	reports: 'reports', charts: 'reports', dashboards: 'reports',
	cards: 'cards',
	form: 'settings', notifications: 'settings', 'multi-add': 'settings',
};

/** Which tab a path under `/collections/{id}` is in; Home for the bare route and anything unmapped. */
export function sectionKeyOf(pathname, collectionId) {
	const prefix = `/collections/${collectionId}`;
	const path = String(pathname || '');
	if (!collectionId || !path.startsWith(prefix)) return 'home';
	const segment = path.slice(prefix.length).split('/').filter(Boolean)[0];
	return SECTION_OF_SEGMENT[segment] || 'home';
}

/**
 * Where the collection switcher goes (KPR-640): the SAME section of the chosen
 * collection when it has one (Items to Items, an item page to Items), else its
 * Home. "Has it" is the shell's own tab rule, so a section the person could not
 * open there (Reports without chart access, Settings without manage) is never
 * linked: Home answers instead. Only the section carries over, never a deeper
 * path or a query (an item or a chart of one collection means nothing in another).
 */
export function switchTargetPath(pathname, currentId, target) {
	const targetId = target?._id;
	const tabs = collectionTabs({ collection: target, collectionId: targetId });
	const wanted = sectionKeyOf(pathname, currentId);
	return (tabs.find(tab => tab.key === wanted) || tabs.find(tab => tab.key === 'home')).to;
}
